import { useEffect, useRef, useState } from "react";

export type VisitorConsentPdfPreviewState = "loading" | "ready" | "unavailable";

const revokePreviewObjectUrl = (objectUrlRef: { current: string | null }) => {
  if (!objectUrlRef.current) {
    return;
  }
  URL.revokeObjectURL(objectUrlRef.current);
  objectUrlRef.current = null;
};

export function useVisitorConsentPdfPreviewUrl(fileViewerUrl: string): {
  previewState: VisitorConsentPdfPreviewState;
  previewUrl: string | null;
} {
  const [previewState, setPreviewState] = useState<VisitorConsentPdfPreviewState>("loading");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    revokePreviewObjectUrl(objectUrlRef);

    const loadPreview = async () => {
      setPreviewState("loading");
      setPreviewUrl(null);

      try {
        const response = await fetch(fileViewerUrl);
        if (!response.ok) {
          throw new Error("PDF preview fetch failed");
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        objectUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
        setPreviewState("ready");
      } catch {
        if (!cancelled) {
          setPreviewState("unavailable");
        }
      }
    };

    loadPreview().catch(() => {
      if (!cancelled) {
        setPreviewState("unavailable");
      }
    });

    return () => {
      cancelled = true;
      revokePreviewObjectUrl(objectUrlRef);
    };
  }, [fileViewerUrl]);

  return { previewState, previewUrl };
}
